import type {Nullable} from '../../../utils/utility-types';

type Cursor = string;

type Params = any;

type Response = any;

export abstract class ApiCollectionState<P = Params, R = Response, C = Cursor> {
  abstract state: ApiCollectionState.State<C>;

  abstract configure(config?: ApiCollectionState.InitConfig): this;

  /**
   * Returns next params for API request merged with provided params
   */
  abstract getNextApiParams(params?: P): P;

  /**
   * Builds params for a next request using next direction
   */
  abstract next(config?: ApiCollectionState.BuildStateConfig<C>): this;

  /**
   * Parses response from API and updates state
   */
  abstract parseApiResponse(response: R): this;

  /**
   * Builds params for a next request using prev direction
   */
  abstract prev(config?: ApiCollectionState.BuildStateConfig<C>): this;

  /**
   * Resets state to initial
   */
  abstract reset(): this;
}

export namespace ApiCollectionState {
  export type BuildStateConfig<C> = {
    /**
     * Cursor to use for the next API request (setiing this will overwrite the pagination flow)
     */
    cursor?: C;
    /**
     * Limit of items to fetch during the next API request
     */
    limit?: number;
  };

  export type InitConfig = {
    /**
     * If true, the request considered to be cursor-based and uses cursor for pagination
     */
    isCursorBased?: boolean;
  };

  /**
   * State of the request
   *
   * Imagine a list of items sorted from newest to oldest. The newest item is at the top.
   * Given that, the "next" direction is at the bottom and the "prev" direction is at the top.
   */
  export type State<C> = {
    cursor: {
      next: Nullable<C>;
      prev: Nullable<C>;
    };
    entitiesCount: number;
    has: {
      next: boolean;
      prev: boolean;
    };
  };
}
